import React from 'react';
import { useTranslate } from 'react-polyglot';
import { useQuery } from '@tanstack/react-query';

import styles from './CombinationAssessment.module.scss';
import { useAuth } from '../../../../Auth';
import Skeleton from '../../../Shared/Skeleton/Skeleton';
import { useAxios } from '../../../../AxiosHandler';

// «Kombinasjonsvurdering»: the dam + sire against the breed's breeding
// criteria (backend: supportmodules/breedingcriteria.js). Breeds without
// criteria, and users without access, get nothing.

type Status = "pass" | "fail" | "unknown" | "warning" | "info";

class Criterion {
    key!: string;
    test?: string;
    kind!: "rule" | "warning" | "info";
    status!: Status;
    threshold!: any;
    values!: any;
}

class Assessment {
    breedId!: number;
    hasRules!: boolean;
    overall?: "pass" | "fail" | "unknown";
    criteria?: Criterion[];
}

const ICONS: { [status: string]: string } = {
    pass: "✓",
    fail: "✕",
    unknown: "?",
    warning: "!",
    info: "i",
};

function CombinationAssessment(props: CombinationAssessmentProps) {
    const t = useTranslate();
    const auth = useAuth();
    const axios = useAxios().axios;

    const { data: assessment, isLoading } = useQuery<Assessment | null>({
        queryKey: ['combination', props.damId, props.sireId],
        queryFn: () => axios.single({
            method: 'get',
            url: "/api/statistics/combination/" + props.damId + "/" + props.sireId,
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res: any) => (res.data && !Array.isArray(res.data) ? res.data : null)),
        staleTime: 5 * 60 * 1000,
    });

    const k = (key: string, options?: any) => t("selectedrequestbreeding.combination." + key, options);
    const number = (value: any, digits: number = 2) => Number(value).toLocaleString("nb-NO", { maximumFractionDigits: digits });
    const who = (side: "dam" | "sire") => k("who." + side);
    const grade = (value: string) => k("grades." + value);

    // Shaped like the finished section: badge, conclusion box, criteria
    if (isLoading) {
        return (
            <section className={styles.assessment} aria-busy="true" aria-label={k("loading")}>
                <div className={styles.header}>
                    <h2>{k("title")}</h2>
                    <Skeleton width={150} height={24} />
                </div>
                <div className={styles.summary + " " + styles.unknown}>
                    <Skeleton circle height={34} />
                    <div className={styles.loadinglines}>
                        <Skeleton width="35%" height={14} />
                        <Skeleton width="80%" height={12} />
                    </div>
                </div>
                <div className={styles.criteria}>
                    {[0, 1, 2, 3, 4, 5, 6].map(i => (
                        <div key={i} className={styles.criterion}>
                            <Skeleton circle height={26} />
                            <div className={styles.loadinglines}>
                                <Skeleton width={140 + (i % 3) * 25} height={12} />
                                <Skeleton width={70 + (i % 2) * 30} height={11} />
                            </div>
                        </div>
                    ))}
                </div>
            </section>
        );
    }
    if (!assessment?.hasRules || !assessment.criteria) return null;

    function title(c: Criterion) {
        switch (c.key) {
            case "genetested": return k("criteria.genetested", { test: k("tests." + c.test) });
            case "genefree": return k("criteria.genefree", { test: k("tests." + c.test) });
            case "showgrade": return k("criteria.showgrade", { grade: grade(c.threshold) });
            case "ebvband": return k("criteria.ebvband", { moment: c.test });
            case "repeatlitter": return k("criteria.repeatlitter", { min: c.threshold });
            default: return k("criteria." + c.key);
        }
    }

    // The line under the title: the value, and why it failed or what is missing
    function value(c: Criterion) {
        const v = c.values ?? {};
        const missing = (sides: ("dam" | "sire")[]) => {
            const lacking = sides.filter(side => v[side] == null);
            return lacking.length == 2 || lacking.length == 0
                ? k("values.missing")
                : k("values.missingfor", { who: who(lacking[0]) });
        };
        switch (c.key) {
            case "inbreeding5":
                return c.status == "unknown" ? k("values.missing")
                    : k("values.inbreeding5", { value: number(v.combination), max: number(c.threshold, 3) });
            case "hdindexsum":
                return c.status == "unknown" ? missing(["dam", "sire"])
                    : k("values.hdindexsum", { value: v.combination, min: c.threshold });
            case "genetested":
                if (c.status == "pass") return k("values.yes");
                if (c.status == "unknown") return missing(["dam", "sire"]);
                return k("values.genetestedno", { dam: v.dam, sire: v.sire });
            case "genefree": {
                // The dogs with a positive test in each line: «Minni (Bærer), Trym (Bærer)»
                if (c.status == "pass") return k("values.yes");
                const list = (dogs: { name: string, result: string }[]) => dogs.map(d => d.name + " (" + d.result + ")").join(", ");
                return k("values.genefreeno", { dam: list(v.dam), sire: list(v.sire) });
            }
            case "showgrade": {
                if (c.status == "pass") return k("values.yes");
                const below = (["dam", "sire"] as const).find(side =>
                    v[side] != null && ["Exc", "VG", "G", "S", "D"].indexOf(v[side]) > ["Exc", "VG", "G", "S", "D"].indexOf(c.threshold));
                if (below) return k("values.showgradeno", { who: who(below), grade: grade(v[below]) });
                return missing(["dam", "sire"]);
            }
            case "ebvband":
                if (c.status == "unknown") return missing(["dam", "sire"]);
                return k("values.ebvband", { value: number(v.combination), min: c.threshold });
            case "repeatlitter": {
                const litters = (v.litters ?? []) as { date: string, pups: number }[];
                if (litters.length == 0) return k("values.no");
                const list = litters.map(l => k("values.litter", { year: new Date(l.date).getFullYear(), pups: l.pups })).join(", ");
                return (c.status == "warning" ? k("values.yes") : k("values.no")) + ": " + list;
            }
            default:
                return "";
        }
    }

    // Warnings that did not trigger, and information (shown, not assessed),
    // get the grey «i», as in the drawing
    const iconStatus = (c: Criterion) => (c.kind == "warning" && c.status == "pass") || c.status == "info" ? "info" : c.status;

    const rules = assessment.criteria.filter(c => c.kind == "rule");
    const failed = rules.filter(c => c.status == "fail").length;
    const unknown = rules.filter(c => c.status == "unknown").length;
    const overall = assessment.overall ?? "unknown";

    return (
        <section className={styles.assessment}>
            <div className={styles.header}>
                <h2>{k("title")}</h2>
                <span className={styles.badge + " " + styles[overall]}>
                    <span className={styles.badgeicon}>{ICONS[overall == "unknown" ? "unknown" : overall]}</span>
                    {k("badge." + overall)}
                </span>
            </div>
            <div className={styles.summary + " " + styles[overall]}>
                <span className={styles.summaryicon}>{ICONS[overall]}</span>
                <div>
                    <div className={styles.summarytitle}>{k("summary.title")}</div>
                    <div className={styles.summarytext}>
                        {overall == "pass" && k("summary.pass")}
                        {overall == "fail" && k("summary.fail", { count: failed, total: rules.length })}
                        {overall == "unknown" && k("summary.unknown", { count: unknown, total: rules.length })}
                        {" "}<em>{k("summary.note")}</em>
                    </div>
                </div>
            </div>
            <div className={styles.criteria}>
                {assessment.criteria.map(c => (
                    <div key={c.key + (c.test ?? "")} className={styles.criterion}>
                        <span className={styles.status + " " + styles[iconStatus(c)]}>{ICONS[iconStatus(c)]}</span>
                        <div className={styles.criteriontext}>
                            <div className={styles.criteriontitle}>{title(c)}</div>
                            <div className={styles.criterionvalue}>{value(c)}</div>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
}

class CombinationAssessmentProps {
    damId!: number;
    sireId!: number;
}

export default CombinationAssessment;
